import React, { useContext } from 'react'
import Workflow from './Workflow'
import { listsService, teamBrandService } from '../services'
import { useRouter } from '../hooks'
import { Avatar } from '../components'
import helpers from '../util/helpers'
import { AppContext } from '../contexts/AppContext'
import { device } from '../core'
import _ from 'lodash'

const TeamBrandWorkflow: React.FC = () => {
  const { replaceURL } = useRouter()
  const { state, dispatch } = useContext(AppContext)
  const { accounts, leagues } = state

  const initialData = {
    timezone: device.timezone,
  }

  const complete = (data: any) =>
    helpers.withLoading(async () => {
      const { avatar, ...rest } = data
      let res = await teamBrandService.create({
        accountId: accounts?.[0]?.id,
        leagueId: _.orderBy(leagues, 'createdAt')?.[0].id,
        ...rest,
      })

      if (avatar) {
        res = await teamBrandService.uploadAvatar(res.id, avatar)
      }

      dispatch({
        type: 'teamBrandAdded',
        value: res,
      })

      replaceURL('/a/dashboard/')
    })

  return (
    <Workflow
      id="team-brand"
      autosave
      required={state.user?.teamBrands?.length === 0}
      steps={[
        {
          id: 'account',
          fields: [
            {
              key: 'accountId',
              type: 'pick-one',
              label: 'Account',
              options: accounts?.map((it) => [it.id, it.name]),
            },
          ],
          title: `Which account?`,
          subtext: `It looks like you have access to multiple accounts. Which account do you want to create this program on?`,
        },
        {
          id: 'program',
          fields: [
            {
              key: 'programType',
              type: 'pick-one',
              label: 'Program Type',
              options: listsService.programTypes,
            },
          ],
          validate: (data: any) => {
            if (!data.programType) {
              return {
                valid: false,
                message: 'Please select your program type.',
              }
            }

            return {
              valid: true,
            }
          },
          getNextStepId: (data: any) => {
            if (['club', 'other'].indexOf(data.programType) > -1) {
              return {
                id: 'about',
                track: 'other',
              }
            }

            return {
              id: 'about',
              track: 'school',
            }
          },
          title: `Let's get started`,
          subtext:
            'What type is your program? This will help us tailor your setup process.',
        },
      ]}
      tracks={[
        {
          id: 'school',
          steps: [
            {
              id: 'about',
              fields: [
                {
                  key: 'name',
                  type: 'input',
                  label: 'School Name',
                  required: true,
                },
                {
                  key: 'mascot',
                  type: 'input',
                  label: 'School Mascot',
                  required: true,
                },
                {
                  key: 'gender',
                  type: 'select',
                  label: 'Program Gender',
                  options: listsService.teamGenders,
                  required: true,
                },
              ],
              title: `Great!`,
              subtext: `Now tell us a little bit more about your school's program.`,
            },
            {
              id: 'details',
              fields: [
                {
                  key: 'a-title',
                  type: 'custom',
                  render() {
                    return (
                      <h4 key="a-title" style={{ color: 'black' }}>
                        School Mascot
                      </h4>
                    )
                  },
                },
                {
                  key: 'avatar',
                  type: 'custom',
                  render(entity: any, save: any) {
                    return (
                      <Avatar
                        key="avatar"
                        size={96}
                        src={
                          entity.avatar
                            ? URL.createObjectURL(entity.avatar)
                            : null
                        }
                        style={{
                          margin: '15px auto',
                        }}
                        allowEdit
                        onImageSelected={async (file: File) => save(file)}
                      />
                    )
                  },
                },
                {
                  key: 'color1Name',
                  type: 'input',
                  label: 'Color',
                },
                {
                  key: 'color2Name',
                  type: 'input',
                  label: 'Color',
                },
                {
                  key: 'color3Name',
                  type: 'input',
                  label: 'Color',
                },
                {
                  key: 'color4Name',
                  type: 'input',
                  label: 'Color',
                },
              ],
              title: `Got it!`,
              subtext: `Tell us about the look of your team. This step is optional.`,
            },
          ],
        },
        {
          id: 'other',
          steps: [
            {
              id: 'about',
              fields: [
                {
                  key: 'name',
                  type: 'input',
                  label: 'Program Name',
                  required: true,
                },
                {
                  key: 'mascot',
                  type: 'input',
                  label: 'Program Mascot',
                  required: true,
                },
                {
                  key: 'gender',
                  type: 'select',
                  label: 'Program Gender',
                  options: listsService.teamGenders,
                  required: true,
                },
                {
                  key: 'timezone',
                  type: 'select',
                  label: 'Timezone',
                  options: listsService.timezones,
                  required: true,
                },
              ],
              title: `Awesome!`,
              subtext: 'Now tell us a little bit more about your program.',
            },
          ],
        },
      ]}
      startIndex={(accounts?.length ?? 0) > 1 ? 0 : 1}
      data={initialData}
      onComplete={complete}
    />
  )
}

export default React.memo(TeamBrandWorkflow)
